/**
 * FeedbackModal - Modal for collecting feedback with star rating
 *
 * Shows when user clicks "Rate" to collect star rating and optional feedback text.
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { X, Loader2 } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { StarRating } from './StarRating'

interface FeedbackModalProps {
  isOpen: boolean
  isLoading: boolean
  onSubmit: (feedback: string, rating: number | null) => void
  onClose: () => void
  initialRating?: number | null
}

export function FeedbackModal({ isOpen, isLoading, onSubmit, onClose, initialRating = null }: FeedbackModalProps) {
  const { t } = useTranslation()
  const [feedbackText, setFeedbackText] = useState('')
  const [rating, setRating] = useState<number | null>(initialRating)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  // Reset feedback text and rating when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFeedbackText('')
      setRating(initialRating)
    }
  }, [isOpen, initialRating])

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isLoading, onClose])

  const handleSubmit = () => {
    if (!isLoading) {
      onSubmit(feedbackText, rating)
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      data-screenshot-exclude
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) {
          onClose()
        }
      }}
    >
      <div
        className="bg-white dark:bg-dark-card rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-dark-border">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
            {t('chatv2:feedback.rate_title', { defaultValue: 'Rate this response' })}
          </h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={clsx(
              'p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
          >
            <X className="h-5 w-5 text-gray-500 dark:text-dark-text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Star Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('chatv2:feedback.rate_question', { defaultValue: 'How would you rate this response?' })}
            </label>
            <StarRating
              value={rating}
              onChange={setRating}
              disabled={isLoading}
              size="lg"
            />
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('chatv2:feedback.additional_label', { defaultValue: 'Additional feedback (optional)' })}
            </label>
            <textarea
              ref={textareaRef}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder={t('chatv2:feedback.additional_placeholder', { defaultValue: 'Tell us more about your experience...' })}
              className={clsx(
                'w-full h-24 px-3 py-2 text-sm bg-white dark:bg-dark-muted text-gray-900 dark:text-dark-text',
                'border border-gray-300 dark:border-gray-600 rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent',
                'resize-none',
                isLoading && 'opacity-50'
              )}
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 bg-gray-50 dark:bg-dark-muted border-t border-gray-200 dark:border-dark-border">
          <button
            onClick={onClose}
            disabled={isLoading}
            className={clsx(
              'px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300',
              'bg-white dark:bg-dark-card border border-gray-300 dark:border-gray-600 rounded-lg',
              'hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {t('chatv2:feedback.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || rating === null}
            className={clsx(
              'px-4 py-2 text-sm font-medium text-white',
              'bg-brand rounded-lg',
              'hover:bg-brand-hover transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'flex items-center gap-2'
            )}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('chatv2:feedback.submitting', { defaultValue: 'Submitting...' })}
              </>
            ) : (
              t('chatv2:feedback.submit_rating', { defaultValue: 'Submit Rating' })
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default FeedbackModal
